import { Divider, Drawer, message, Steps, Tag, Typography } from 'antd';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import portalAPI from '../../api/api';
import { GET_CLAIM_DETAIL_URL } from '../../api/URLs';
import moment from 'moment';
import { formatToCurrency, formatWrapAddress, maskAccountNumber } from '../../exports/util';
import CommentsSection from '../comments/comments-section';
import UploadDocModal from '../upload/upload-doc-modal';
import LilyButton from '../button/lily-button';
import { useNavigate } from 'react-router-dom';
import CopyInfoComponent from '../copay-info.component';
import { useSelector } from 'react-redux';

const ViewClaimDetails = forwardRef(({ }, ref) => {
    const user = useSelector((state) => state.authenticatedUser);
    const navigate = useNavigate();
    const { Title, Text } = Typography;
    useImperativeHandle(ref, () => {
        return {
            open: ({ caseNumber }) => {
                setClaimId(caseNumber);
                setDrawerOpen(true);
            }, close: () => {
                setDrawerOpen(false);
            }
        };
    });

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [caseNumber, setClaimId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [claimData, setClaimData] = useState({});

    const fetchClaimDetail = async (caseNumber) => {
        setIsLoading(true);
        try {
            let URL = GET_CLAIM_DETAIL_URL.replace(":id", caseNumber);
            let rsp = await portalAPI.get(URL);
            if (rsp.data.status === 200) {
                setClaimData(rsp.data.data);
            }
        } catch (error) {
            message.error(`Failed to fetch claim detail.`);
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (!caseNumber) return;
        fetchClaimDetail(caseNumber);
    }, [caseNumber]);

    const handlePing = async () => {
        await fetchClaimDetail(caseNumber);
    }

    const renderTagStatus = (status) => {
        switch (status) {
            case "open":
                return <Tag color="success">Open</Tag>;
            case "under-review":
                return <Tag color="processing">Under Review</Tag>;
            case "approved":
                return <Tag color="success">Approved</Tag>;
            case "paid":
                return <Tag color="success">Paid</Tag>;
            case "rejected":
                return <Tag color="error">Rejected</Tag>;
            default:
                return null;
        }
    };

    const claimStatus = {
        "open": "Open",
        "under-review": "Under Review",
        "approved": "Approved",
        "paid": "Paid",
        "rejected": "Rejected"
    }

    const filledDetails = (data) => {
        let reimbursementMethod = "";
        if (!data.insuranceCard && user?.insuranceCard) {
            data['insuranceCard'] = user?.insuranceCard
        }

        if (data?.paymentInfo) {
            let method = data?.paymentInfo?.method;
            let typeMethod = {
                "check-by-mail": "Check by Mail:",
                "direct-deposit": "Direct Deposit"
            }
            if (method === "check-by-mail") {
                let address = data?.paymentInfo?.checkByMail;
                reimbursementMethod = <div className='d-flex flex-column'>
                    <Text>{typeMethod[method]}</Text>
                    <div className='px-3 d-flex flex-column'>
                        {formatWrapAddress({
                            line1: address.address1,
                            line2: address.address2,
                            city: address.city,
                            state: address.state,
                            zip: address.zip,
                        })}
                    </div>
                </div>
            }
            if (method === "direct-deposit") {
                reimbursementMethod = `${typeMethod[method]} to ${data?.paymentInfo?.directDeposit?.accountName} ${data?.paymentInfo?.directDeposit?.accountNumber ? maskAccountNumber(data?.paymentInfo?.directDeposit?.accountNumber) : ''}`
            }
        }

        let fields = [
            {
                title: <Text>Status:</Text>,
                element: <div>{data?.status && claimStatus[data?.status] || ''}</div>
            },
            {
                title: <Text>Date:</Text>,
                element: <div>
                    {data?.createdAt ? moment(data?.createdAt).format("MM/DD/YYYY") : 'N/A'}
                </div>
            },
            {
                title: <Text>Amount Requested:</Text>,
                element: <div>{formatToCurrency(data?.amountRequested)}</div>
            },
        ]

        if (data?.amountPaid) {
            fields.push(
                {
                    title: <Text>Amount Paid:</Text>,
                    element: <div>{formatToCurrency(data?.amountPaid)}</div>
                },
            )
        }

        fields.push(
            {
                title: <Text>Reimbursement Method:</Text>,
                classEle: "d-flex flex-column",
                element: <div className='ms-2'>{reimbursementMethod}</div>
            },
            {
                title: <Text>Copay Card Processing Information:</Text>,
                classEle: "d-flex flex-column",
                element: <CopyInfoComponent data={data} />
            },
            {
                title: <Text>Claim Form:</Text>,
                element: <div>
                    {
                        data?.previewFormLink?.map((item) => {
                            return (
                                <a style={{ color: 'var(--primary-color)' }} href={item.presignedUrl} target="_blank">form_preview.pdf</a>
                            )
                        })
                    }
                </div>
            }
        )

        return fields;
    }

    return (
        <div>
            <Drawer
                title=""
                closable={{ 'aria-label': 'Close Button' }}
                onClose={() => setDrawerOpen(false)}
                open={drawerOpen}
                width={'500px'}
                loading={isLoading}
            >
                <div className='d-flex flex-column gap-3'>
                    <div className='d-flex flex-row align-items-center gap-3'>
                        <Title className='m-0' level={4} style={{ fontWeight: '500' }}>
                            Claim No. {caseNumber}
                        </Title>
                        {renderTagStatus(claimData?.status || '')}
                    </div>
                    {
                        claimData && claimData.status === 'rejected' && (
                            <LilyButton style={{ minWidth: '200px', width: 'fit-content', padding: '0 20px' }} onClick={() => navigate(`/claims-processing/resubmit/${caseNumber}`)} text="Resubmit as a New Claim" />
                        )
                    }
                    <div className='d-flex flex-column gap-1'>
                        {
                            filledDetails(claimData).map((item) => {
                                return (
                                    <div className={`${item.classEle ? item.classEle : 'd-flex flex-row gap-2'}`}>
                                        {item.title}
                                        {item.element}
                                    </div>
                                )
                            })
                        }
                    </div>
                    <Divider className='m-0' />
                    <div className='d-flex flex-column gap-2'>
                        <Title className='m-0' level={5} style={{ fontWeight: '500' }}>
                            Supporting Documents
                        </Title>
                        <div className='ms-2'>
                            {
                                claimData?.proofOfPayment?.length > 0 && (
                                    <div className='d-flex flex-column'>
                                        <Text style={{ fontWeight: 600 }}>Original Pharmacy Receipt</Text>
                                        {
                                            claimData?.proofOfPayment?.map((item) => {
                                                return (
                                                    <a style={{ color: 'var(--primary-color)' }} href={item.presignedUrl} target="_blank">{item.fileName}</a>
                                                )
                                            })
                                        }
                                    </div>
                                )
                            }
                            {
                                claimData?.eob?.length > 0 && (
                                    <div className='d-flex flex-column'>
                                        <Text style={{ fontWeight: 600 }}>EOB</Text>
                                        {
                                            claimData?.eob?.map((item) => {
                                                return (
                                                    <a style={{ color: 'var(--primary-color)' }} href={item.presignedUrl} target="_blank">{item.fileName}</a>
                                                )
                                            })
                                        }
                                    </div>
                                )
                            }
                            {
                                claimData?.insuranceCards?.length > 0 && (
                                    <div className='d-flex flex-column'>
                                        <Text style={{ fontWeight: 600 }}>Primary Insurance Card</Text>
                                        {
                                            claimData?.insuranceCards?.map((item) => {
                                                return (
                                                    <a style={{ color: 'var(--primary-color)' }} href={item?.file?.presignedUrl} target="_blank">{item?.file?.fileName}</a>
                                                )
                                            })
                                        }
                                    </div>
                                )
                            }
                            {
                                claimData?.reimbursementForm?.length > 0 && (
                                    <div className='d-flex flex-column'>
                                        <Text style={{ fontWeight: 600 }}>Reimbursement Form</Text>
                                        {
                                            claimData?.reimbursementForm?.map((item) => {
                                                return (
                                                    <a style={{ color: 'var(--primary-color)' }} href={item?.file?.presignedUrl} target="_blank">{item?.file?.fileName}</a>
                                                )
                                            })
                                        }
                                    </div>
                                )
                            }
                            {
                                claimData?.prescriptionLabel?.length > 0 && (
                                    <div className='d-flex flex-column'>
                                        <Text style={{ fontWeight: 600 }}>Prescription Label</Text>
                                        {
                                            claimData?.prescriptionLabel?.map((item) => {
                                                return (
                                                    <a style={{ color: 'var(--primary-color)' }} href={item?.file?.presignedUrl} target="_blank">{item?.file?.fileName}</a>
                                                )
                                            })
                                        }
                                    </div>
                                )
                            }
                            {
                                claimData?.additionalDocs?.length > 0 && (
                                    <div className='d-flex flex-column'>
                                        <Text style={{ fontWeight: 600 }}>Additional Documents</Text>
                                        {
                                            claimData?.additionalDocs?.map((item) => {
                                                return (
                                                    <a style={{ color: 'var(--primary-color)' }} href={item?.presignedUrl} target="_blank">{item?.fileName}</a>
                                                )
                                            })
                                        }
                                    </div>
                                )
                            }
                        </div>
                        {
                            !['rejected', 'approved', 'paid'].includes(claimData.status) && (
                                <UploadDocModal caseNumber={claimData?.caseNumber || ''} onPing={handlePing} />
                            )
                        }
                    </div>
                    <Divider className='m-0' />
                    <CommentsSection caseNumber={claimData?.caseNumber || ''} patientName={claimData?.patient?.fullName || ''} record={claimData?.additionalComments || []} />
                    <div className=''>
                        <Title className='m-0' level={5} style={{ fontWeight: '500' }}>
                            Status History
                        </Title>
                        <Steps
                            progressDot
                            current={claimData?.history?.length || 0}
                            direction="vertical"
                            items={
                                claimData?.history?.map((item) => {
                                    return {
                                        title: <Text>{item?.createdAt ? moment(item?.createdAt).format("MMMM D, YYYY") : 'N/A'}</Text>,
                                        description: <Title className='m-0' level={5} style={{ fontWeight: '500' }}>{item.title}</Title>,
                                    }
                                })
                            }
                        />
                        <div />
                    </div>
                </div>
            </Drawer >
        </div >
    )
})

export default ViewClaimDetails